/**
 * Store-wide behaviour, loaded on every page.
 *
 * - Light/dark and larger-text switches (same storage keys as the queue).
 * - Header dropdowns, the narrow-screen menu drawer and the search dialog
 *   with live suggestions.
 * - Country/language pickers submit as soon as they change.
 * - Quantity steppers, and forms marked data-autosubmit (collection
 *   filters, cart quantities) submit themselves after a short pause.
 * - "Add to cart" stays on the page: the item is added in the background,
 *   the cart count updates and a ticket-style popup confirms it.
 * - Toasts (little messages at the bottom, like the queue's) and share.
 *
 * Everything works without JavaScript too; this only makes it smoother.
 */
(() => {
  const root = document.documentElement;

  const save = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Private browsing or storage blocked: the change still applies to this page.
    }
  };

  /* ---------- Queue closing time in the viewer's own time zone ---------- */

  // Same logic as the break queue's sign (my-queue, closingNote()): today's
  // closing hour in Los Angeles, shown in the viewer's time zone. Viewers in
  // Pacific time see just the main sentence.
  const LA = 'America/Los_Angeles';
  document.querySelectorAll('[data-closing-note]').forEach((note) => {
    try {
      if (Intl.DateTimeFormat().resolvedOptions().timeZone === LA) return;
      const hour = Number(note.dataset.closingHour) || 20;
      const parts = {};
      new Intl.DateTimeFormat('en-US', { timeZone: LA, year: 'numeric', month: 'numeric', day: 'numeric' })
        .formatToParts(new Date())
        .forEach((p) => {
          if (p.type !== 'literal') parts[p.type] = Number(p.value);
        });
      const guess = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, hour, 0));
      const offset =
        new Date(guess.toLocaleString('en-US', { timeZone: LA })) - new Date(guess.toLocaleString('en-US', { timeZone: 'UTC' }));
      const closes = new Date(guess.getTime() - offset);
      const time = closes.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
      note.querySelector('[data-closing-local-text]').textContent = note.dataset.closingLocal.replace('#', time);
    } catch (e) {
      // Very old browser: the main sentence alone is still right.
    }
  });

  /* ---------- Toasts ---------- */

  const toastRegion = () => {
    let region = document.querySelector('.toasts');
    if (!region) {
      region = document.createElement('div');
      region.className = 'toasts';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.append(region);
    }
    return region;
  };

  const toast = (message, kind = '') => {
    const el = document.createElement('div');
    el.className = 'toast' + (kind ? ' ' + kind : '');
    el.textContent = message;
    const leave = () => {
      el.classList.add('leaving');
      setTimeout(() => el.remove(), 220);
    };
    el.addEventListener('click', leave);
    toastRegion().append(el);
    setTimeout(leave, kind === 'error' ? 7000 : 4000);
  };

  window.theme = Object.assign(window.theme || {}, { toast });

  /* ---------- Light/dark and larger text ---------- */

  const syncTextToggle = () => {
    const large = root.getAttribute('data-text') === 'large';
    document.querySelectorAll('[data-text-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(large));
    });
  };
  syncTextToggle();

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-theme-toggle]')) {
      const next = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
      root.setAttribute('data-theme', next);
      save('theme', next);
    }

    if (event.target.closest('[data-text-toggle]')) {
      const next = root.getAttribute('data-text') === 'large' ? 'normal' : 'large';
      root.setAttribute('data-text', next);
      save('textSize', next);
      syncTextToggle();
    }
  });

  /* ---------- Header: one row or two ---------- */

  // Start stacked (no overlap before this runs), try one row, and go back
  // to stacked if the menu doesn't fit beside the logo and icons.
  const header = document.querySelector('[data-header]');
  if (header) {
    const menu = header.querySelector('.site-header__menu .menu');
    const fit = () => {
      if (!menu || getComputedStyle(menu.parentElement).display === 'none') return;
      header.classList.remove('is-stacked');
      if (menu.scrollWidth > menu.clientWidth + 1) header.classList.add('is-stacked');
    };
    fit();
    if ('ResizeObserver' in window) {
      let last = header.clientWidth;
      new ResizeObserver(() => {
        if (header.clientWidth === last) return;
        last = header.clientWidth;
        fit();
      }).observe(header);
    }
    if (document.fonts) document.fonts.ready.then(fit);
  }

  /* ---------- Header dropdowns ---------- */

  const drops = () => document.querySelectorAll('[data-menu-drop]');

  const closeDrops = (except) => {
    drops().forEach((d) => {
      if (d !== except) d.open = false;
    });
  };

  drops().forEach((d) => {
    d.addEventListener('toggle', () => {
      if (d.open) closeDrops(d);
    });
    d.addEventListener('focusout', (event) => {
      if (!d.contains(event.relatedTarget) && event.relatedTarget) d.open = false;
    });
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-menu-drop]')) closeDrops();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-menu-drop][open]');
    if (open) {
      open.open = false;
      open.querySelector('summary').focus();
    }
  });

  /* ---------- Dialogs: menu drawer and search ---------- */

  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    if (opener) opener.setAttribute('aria-expanded', 'true');
    dialog.addEventListener(
      'close',
      () => {
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          opener.focus();
        }
      },
      { once: true }
    );
  };

  document.addEventListener('click', (event) => {
    const drawerOpener = event.target.closest('[data-drawer-open]');
    if (drawerOpener) {
      openDialog(document.querySelector('[data-drawer]'), drawerOpener);
      return;
    }

    const searchOpener = event.target.closest('[data-search-open]');
    const searchDialog = document.querySelector('[data-search-dialog]');
    if (searchOpener && searchDialog) {
      event.preventDefault();
      openDialog(searchDialog, searchOpener);
      searchDialog.querySelector('input[type="search"]').focus();
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      closer.closest('dialog').close();
      return;
    }

    // A click on the dimmed backdrop lands on the dialog itself.
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const box = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) event.target.close();
    }
  });

  // The drawer is only for narrow screens: close it if the window widens.
  const wide = window.matchMedia('(min-width: 990px)');
  wide.addEventListener('change', () => {
    const drawer = document.querySelector('[data-drawer]');
    if (wide.matches && drawer && drawer.open) drawer.close();
  });

  /* ---------- Search suggestions ---------- */

  const searchDialog = document.querySelector('[data-search-dialog]');
  if (searchDialog) {
    const form = searchDialog.querySelector('form');
    const input = form.querySelector('input[type="search"]');
    const results = searchDialog.querySelector('[data-search-results]');
    const status = searchDialog.querySelector('[data-search-status]');
    const strings = {};
    searchDialog.querySelector('[data-search-strings]').content.querySelectorAll('[data-key]').forEach((el) => {
      strings[el.dataset.key] = el.textContent.trim();
    });
    let timer;
    let controller;

    const clear = () => {
      results.replaceChildren();
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      searchDialog.removeAttribute('aria-busy');
    };

    const group = (title, items, render) => {
      if (!items || items.length === 0) return null;
      const section = document.createElement('div');
      section.className = 'suggest__group';
      const heading = document.createElement('p');
      heading.className = 'suggest__head';
      heading.textContent = title;
      const list = document.createElement('ul');
      list.className = 'suggest__list';
      list.setAttribute('role', 'presentation');
      items.forEach((item) => {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        const a = document.createElement('a');
        a.className = 'suggest__item';
        a.href = item.url;
        a.setAttribute('role', 'option');
        render(a, item);
        li.append(a);
        list.append(li);
      });
      section.append(heading, list);
      return section;
    };

    const textSpan = (cls, text) => {
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = text;
      return span;
    };

    const show = (data, query) => {
      const r = data.resources.results;
      const nodes = [
        group(strings.products, r.products, (a, p) => {
          const pic = document.createElement('span');
          pic.className = 'suggest__pic';
          if (p.image) {
            const img = document.createElement('img');
            const src = new URL(p.image, location.href);
            src.searchParams.set('width', '120');
            img.src = src.href;
            img.alt = '';
            img.width = 56;
            img.height = 56;
            img.loading = 'lazy';
            pic.append(img);
          }
          const info = document.createElement('span');
          info.className = 'suggest__info';
          info.append(textSpan('suggest__title', p.title));
          a.append(pic, info);
        }),
        group(strings.collections, r.collections, (a, c) => a.append(textSpan('suggest__title', c.title))),
        group(strings.pages, r.pages, (a, p) => a.append(textSpan('suggest__title', p.title))),
      ].filter(Boolean);

      const count = (r.products || []).length + (r.collections || []).length + (r.pages || []).length;
      results.replaceChildren(...nodes);

      if (count === 0) {
        results.append(textSpan('suggest__none', strings.none));
      }

      const all = document.createElement('button');
      all.type = 'button';
      all.className = 'btn suggest__all';
      all.textContent = strings.all.replace('#', query);
      all.addEventListener('click', () => form.requestSubmit());
      results.append(all);

      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      status.textContent = strings.count.replace('#', String(count));
    };

    const suggest = async () => {
      const query = input.value.trim();
      if (controller) controller.abort();
      if (query.length < 2) {
        clear();
        return;
      }
      controller = new AbortController();
      searchDialog.setAttribute('aria-busy', 'true');
      const url = new URL(form.dataset.predictiveUrl + '.json', location.href);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,page');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      try {
        const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(String(res.status));
        show(await res.json(), query);
      } catch (err) {
        if (err.name !== 'AbortError') clear();
      } finally {
        searchDialog.removeAttribute('aria-busy');
      }
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(suggest, 220);
    });

    // Arrow keys move between suggestions.
    searchDialog.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = [...results.querySelectorAll('.suggest__item')];
      if (items.length === 0) return;
      event.preventDefault();
      const at = items.indexOf(document.activeElement);
      let next = event.key === 'ArrowDown' ? at + 1 : at - 1;
      if (next < -1) next = items.length - 1;
      if (next === -1 || next >= items.length) input.focus();
      else items[next].focus();
    });

    searchDialog.addEventListener('close', () => {
      if (controller) controller.abort();
    });
  }

  /* ---------- Country/language pickers ---------- */

  document.addEventListener('change', (event) => {
    const select = event.target.closest('.localization__select');
    if (select) select.form.submit();
  });

  /* ---------- Quantity steppers ---------- */

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step) return;
    const input = step.closest('[data-qty]').querySelector('input');
    const min = input.min === '' ? 0 : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + Number(step.dataset.qtyStep)));
    if (String(next) === input.value) {
      if (Number(step.dataset.qtyStep) > 0) input.dispatchEvent(new CustomEvent('qty:max', { bubbles: true }));
      return;
    }
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Forms that submit themselves ---------- */

  const autoTimers = new WeakMap();

  // The cart saves itself, so its Update button (there for visitors without
  // JavaScript) would only reload the page. It stays in the form as the
  // submitter for the automatic updates.
  document.querySelectorAll('.cart [data-autosubmit-button]').forEach((button) => {
    button.hidden = true;
  });

  // Cart areas that save themselves (queue details, note) say so: a hint
  // while typing, "Saving..." during the reload, then "Saved" afterwards.
  // The page reloads to save, so remember which area and where we were.
  const SAVED_KEY = 'cart-saved';
  const saveArea = (el) => el.closest && el.closest('[data-save-area]');
  const setStatus = (area, text, kind) => {
    const status = area && area.querySelector('[data-save-status]');
    if (!status) return;
    status.textContent = text;
    status.dataset.kind = kind;
  };

  document.addEventListener('input', (event) => {
    const area = saveArea(event.target);
    if (!area || event.target.type === 'radio') return;
    const form = event.target.form;
    if (form) setStatus(area, form.dataset.statusTyping, 'typing');
  });

  try {
    const saved = JSON.parse(sessionStorage.getItem(SAVED_KEY) || 'null');
    sessionStorage.removeItem(SAVED_KEY);
    const area = saved && document.querySelector(`[data-save-area="${saved.area}"]`);
    if (area) {
      const form = area.closest('form');
      if (saved.y) window.scrollTo(0, saved.y);
      setStatus(area, '✓ ' + form.dataset.statusSaved, 'saved');
      const message = saved.area === 'note' ? form.dataset.statusSavedNote : form.dataset.statusSavedQueue;
      toast(message, 'ok');
      setTimeout(() => setStatus(area, '', ''), 5000);
    }
  } catch (e) {
    // No session storage: the page still saves, just without the message.
  }

  const autoSubmit = (event) => {
    const form = event.target.form;
    if (!form || !form.matches('[data-autosubmit]')) return;
    // Notes are saved when you click away from them, not on every key.
    if (event.target.tagName === 'TEXTAREA' && event.type !== 'change') return;
    const area = event.type === 'change' && saveArea(event.target);
    if (area) {
      setStatus(area, form.dataset.statusSaving, 'saving');
      try {
        sessionStorage.setItem(SAVED_KEY, JSON.stringify({ area: area.dataset.saveArea, y: window.scrollY }));
      } catch (e) {
        // Fine without it.
      }
    }
    // Typing a price waits a little longer than ticking a box.
    const typing = event.type === 'input' && event.target.type === 'number';
    if (event.type === 'input' && !typing) return;
    clearTimeout(autoTimers.get(form));
    autoTimers.set(
      form,
      setTimeout(() => {
        form.setAttribute('aria-busy', 'true');
        const submitter = form.querySelector('[data-autosubmit-button]');
        form.requestSubmit(submitter || undefined);
      }, typing ? 900 : 450)
    );
  };
  document.addEventListener('change', autoSubmit);
  document.addEventListener('input', autoSubmit);

  // Empty filter fields would clutter the address, so leave them out.
  document.addEventListener('formdata', (event) => {
    if (!event.target.matches('[data-clean-empty]')) return;
    for (const [key, value] of [...event.formData.entries()]) {
      if (value === '') event.formData.delete(key);
    }
  });

  /* ---------- Queue details: username + where they watch the most ---------- */

  // Everyone gives these before buying (snippets/queue-profile.liquid has the
  // details). Signed-in customers missing either get the prompt on every page
  // until it's done, and nobody can add to the cart without them. Answers go
  // on the cart as "Username" / "Watches on", so every order carries them; the
  // queue (my-queue, shopifyOrder) saves them to the customer's profile when
  // they buy, where the profile is empty. The username rule matches the queue's.
  const qpDialog = document.querySelector('[data-queue-profile]');
  const QP_NAME = /^[A-Za-z0-9_.-]{3,30}$/;
  let qpWaiting = null;
  let qpCartSynced = false;

  const qpStrings = {};
  if (qpDialog) {
    qpDialog.querySelector('[data-qp-strings]').content.querySelectorAll('[data-key]').forEach((el) => {
      qpStrings[el.dataset.key] = el.textContent.trim();
    });
  }

  const cartBase = () => {
    const popup = document.querySelector('[data-cart-popup]');
    return popup ? popup.dataset.cartUrl : '/cart';
  };

  const setCartQueueInfo = (username, platform) =>
    fetch(cartBase() + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Username: username, 'Watches on': platform } }),
    }).then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      qpCartSynced = true;
    });

  const openQueueProfile = () => {
    if (!qpDialog) return Promise.resolve(true);
    const d = qpDialog.dataset;
    const input = qpDialog.querySelector('input[name="username"]');
    input.value = d.qpUsername || '';
    qpDialog.querySelectorAll('input[name="watchesOn"]').forEach((r) => {
      r.checked = r.value === d.qpPlatform;
    });
    qpDialog.querySelector('[data-qp-error]').hidden = true;
    if (!qpDialog.open) qpDialog.showModal();
    (input.value ? qpDialog.querySelector('input[name="watchesOn"]') : input).focus();
    return new Promise((resolve) => {
      qpWaiting = resolve;
    });
  };

  // Before adding to the cart: true to carry on, false to stop.
  const ensureQueueInfo = async () => {
    if (!qpDialog) return true;
    const d = qpDialog.dataset;
    if (d.qpComplete !== 'true') return openQueueProfile();
    if (!qpCartSynced) await setCartQueueInfo(d.qpUsername, d.qpPlatform).catch(() => {});
    return true;
  };

  if (qpDialog) {
    const form = qpDialog.querySelector('[data-qp-form]');
    const error = qpDialog.querySelector('[data-qp-error]');
    const saveButton = qpDialog.querySelector('[data-qp-save]');
    const fail = (message) => {
      error.textContent = message;
      error.hidden = false;
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const d = qpDialog.dataset;
      const username = form.username.value.trim().replace(/^@/, '');
      const checked = form.querySelector('input[name="watchesOn"]:checked');
      if (!QP_NAME.test(username)) {
        fail(qpStrings.need_name);
        form.username.focus();
        return;
      }
      if (!checked) {
        fail(qpStrings.need_watch);
        return;
      }
      const platform = checked.value;
      error.hidden = true;
      saveButton.disabled = true;
      saveButton.setAttribute('aria-busy', 'true');

      try {
        await setCartQueueInfo(username, platform);
        d.qpUsername = username;
        d.qpPlatform = platform;
        d.qpComplete = 'true';
        const resolve = qpWaiting;
        qpWaiting = null;
        qpDialog.close();
        toast(qpStrings.saved, 'ok');
        if (resolve) resolve(true);
      } catch (e) {
        fail(qpStrings.failed);
      } finally {
        saveButton.disabled = false;
        saveButton.removeAttribute('aria-busy');
      }
    });

    qpDialog.querySelector('[data-qp-later]').addEventListener('click', () => qpDialog.close());
    qpDialog.addEventListener('close', () => {
      if (qpWaiting) {
        const resolve = qpWaiting;
        qpWaiting = null;
        resolve(false);
      }
    });

    // Signed-in and missing details: ask straight away (right after sign-up or
    // login, and on every page until it's done). The cart page has the same
    // fields in the page, so it doesn't pop up there.
    const d = qpDialog.dataset;
    if (d.qpLoggedIn === 'true' && d.qpComplete !== 'true' && !document.querySelector('form.cart')) {
      openQueueProfile();
    }
  }

  // Cart page: same username rule on the in-page field.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches('form.cart')) return;
    if (event.submitter && event.submitter.hasAttribute('formnovalidate')) return;
    const field = form.querySelector('input[name="attributes[Username]"][type="text"]');
    if (!field) return;
    field.value = field.value.trim().replace(/^@/, '');
    if (!QP_NAME.test(field.value)) {
      event.preventDefault();
      field.setCustomValidity(qpStrings.need_name || '');
      field.reportValidity();
      field.addEventListener('input', () => field.setCustomValidity(''), { once: true });
    }
  });

  /* ---------- Purchase limits ("(LIMIT 2)" in the product title) ---------- */

  // snippets/purchase-limit.liquid puts data-limit, data-limit-product and
  // data-limit-ordered (units ordered in the last 12 hours, signed-in
  // customers only) on the product form wrapper and on each cart line.
  // Here we add what's already in the cart, stop anyone going over, and
  // explain why with a popup.
  const alertBox = document.querySelector('[data-alert]');

  const showAlert = (title, text) => {
    if (!alertBox) return;
    alertBox.querySelector('[data-alert-title]').textContent = title;
    alertBox.querySelector('[data-alert-text]').textContent = text;
    if (!alertBox.open) alertBox.showModal();
  };

  const fill = (template, values) =>
    template
      .replace('%L', values.limit ?? '')
      .replace('%N', values.count ?? '')
      .replace('%H', values.hours ?? '');

  const limitOf = (scope) => ({
    limit: Number(scope.dataset.limit) || 0,
    ordered: Number(scope.dataset.limitOrdered) || 0,
    resetHours: Number(scope.dataset.limitResetHours) || 0,
    product: Number(scope.dataset.limitProduct),
  });

  const fetchCart = () => {
    const popup = document.querySelector('[data-cart-popup]');
    const url = (popup ? popup.dataset.cartUrl : '/cart') + '.js';
    return fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  };

  const inCartFor = (cart, productId) =>
    cart.items.filter((i) => i.product_id === productId).reduce((sum, i) => sum + i.quantity, 0);

  // "This item is limited to 2 per customer per day. You already have 1 in
  // your cart. You can add 1 more." Only shown when they try to go past
  // the limit by adding to the cart, or in the cart itself.
  const limitPopup = (info, canAdd, inCart = 0) => {
    const d = alertBox.dataset;
    const parts = [fill(d.limitBase, { limit: info.limit })];
    if (inCart > 0) parts.push(fill(d.limitInCart, { count: inCart }));
    if (info.ordered > 0) parts.push(fill(d.limitOrdered, { count: info.ordered }));
    if (canAdd > 0) {
      parts.push(fill(d.limitCanAdd, { count: canAdd }));
    } else {
      parts.push(d.limitNone);
      if (info.resetHours > 0) parts.push(fill(d.limitReset, { hours: info.resetHours }));
    }
    showAlert(d.limitTitle, parts.join(' '));
  };

  // Product page: the quantity box stops at what's left today (no popup while
  // pressing +); the limit note flashes so it's clear why it stopped.
  const flashLimitNote = (scope) => {
    const note = scope && scope.querySelector('[data-limit-note]');
    if (!note || note.hidden) return;
    note.classList.remove('is-flash');
    void note.offsetWidth; // restart the animation
    note.classList.add('is-flash');
  };

  const capProductQty = (scope, left) => {
    const input = scope.querySelector('input[name="quantity"]');
    if (!input) return;
    if (input.dataset.ruleMax === undefined) input.dataset.ruleMax = input.max;
    const ruleMax = input.dataset.ruleMax === '' ? Infinity : Number(input.dataset.ruleMax);
    const max = Math.max(1, Math.min(ruleMax, left));
    input.max = max;
    if (Number(input.value) > max) input.value = max;
  };

  // Product page: "Limit 2 per customer per day. 1 in your cart." and a
  // "Limit reached" button once nothing more can be added.
  const refreshProductLimit = async (cart) => {
    const scope = document.querySelector('.product__buy[data-limit]');
    if (!scope || !alertBox) return;
    const info = limitOf(scope);
    if (!cart) cart = await fetchCart().catch(() => null);
    const inCart = cart ? inCartFor(cart, info.product) : 0;
    const d = alertBox.dataset;
    const parts = [fill(d.noteLimit, { limit: info.limit })];
    if (info.ordered > 0) parts.push(fill(d.noteOrdered, { count: info.ordered }));
    if (inCart > 0) parts.push(fill(d.noteCart, { count: inCart }));
    const left = info.limit - info.ordered - inCart;
    if (left <= 0 && info.resetHours > 0) parts.push(fill(d.limitReset, { hours: info.resetHours }));
    const note = scope.querySelector('[data-limit-note]');
    note.textContent = parts.join(' ');
    note.hidden = false;
    capProductQty(scope, left);

    const button = scope.querySelector('[data-add-button]');
    if (button && !button.hasAttribute('data-sold-out')) {
      if (left <= 0) {
        button.disabled = true;
        button.dataset.limitReached = 'true';
        button.textContent = d.labelReached;
      } else if (button.dataset.limitReached) {
        delete button.dataset.limitReached;
        button.disabled = false;
        button.textContent = button.dataset.labelAdd;
      }
    }
  };
  refreshProductLimit();

  // Called before adding to the cart; false means "don't add".
  const allowAdd = async (form, quantity) => {
    const scope = form.closest('[data-limit]');
    if (!scope || !alertBox) return true;
    const info = limitOf(scope);
    const cart = await fetchCart();
    const inCart = inCartFor(cart, info.product);
    const canAdd = Math.max(0, info.limit - info.ordered - inCart);
    if (quantity <= canAdd) return true;
    limitPopup(info, canAdd, inCart);
    const input = form.querySelector('input[name="quantity"]');
    if (input && canAdd > 0) input.value = canAdd;
    return false;
  };

  // Product page quantity box and cart quantities: cap as people type or
  // press +, before the cart form submits itself.
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'number' || !alertBox) return;

      const cartLine = input.closest('.cart-item[data-limit]');
      if (cartLine) {
        const info = limitOf(cartLine);
        const lines = [...document.querySelectorAll('.cart-item[data-limit]')].filter(
          (l) => Number(l.dataset.limitProduct) === info.product
        );
        const others = lines
          .filter((l) => l !== cartLine)
          .reduce((sum, l) => sum + (Number(l.querySelector('input[type="number"]').value) || 0), 0);
        const max = Math.max(0, info.limit - info.ordered - others);
        if (Number(input.value) > max) {
          input.value = max;
          // Hold the cart's auto-update so the page doesn't reload under the
          // popup; save the capped number once it's closed (if it changed).
          event.stopImmediatePropagation();
          limitPopup(info, 0);
          alertBox.addEventListener(
            'close',
            () => {
              if (input.value !== input.defaultValue) {
                input.form.requestSubmit(input.form.querySelector('[data-autosubmit-button]') || undefined);
              }
            },
            { once: true }
          );
        }
        return;
      }

      const scope = input.closest('.product__buy[data-limit]');
      if (scope && input.name === 'quantity') {
        const info = limitOf(scope);
        fetchCart()
          .then((cart) => {
            const canAdd = Math.max(0, info.limit - info.ordered - inCartFor(cart, info.product));
            if (Number(input.value) > canAdd) {
              input.value = Math.max(1, canAdd);
              flashLimitNote(scope);
            }
          })
          .catch(() => {});
      }
    },
    true
  );

  document.addEventListener('qty:max', (event) => {
    flashLimitNote(event.target.closest('.product__buy[data-limit]'));
  });

  // Cart page: if something got past the limit (for example added from
  // another tab), bring it back down to the limit once and explain.
  const cartForm = document.querySelector('form.cart');
  if (cartForm && alertBox) {
    const byProduct = new Map();
    cartForm.querySelectorAll('.cart-item[data-limit]').forEach((line) => {
      const info = limitOf(line);
      if (!byProduct.has(info.product)) byProduct.set(info.product, { info, lines: [] });
      byProduct.get(info.product).lines.push(line);
    });
    let changed = null;
    byProduct.forEach(({ info, lines }) => {
      let left = Math.max(0, info.limit - info.ordered);
      lines.forEach((line) => {
        const input = line.querySelector('input[type="number"]');
        const qty = Number(input.value) || 0;
        const keep = Math.min(qty, left);
        left -= keep;
        if (keep !== qty) {
          input.value = keep;
          changed = info;
        }
      });
    });
    if (changed) {
      limitPopup(changed, 0);
      alertBox.addEventListener(
        'close',
        () => cartForm.requestSubmit(cartForm.querySelector('[data-autosubmit-button]') || undefined),
        { once: true }
      );
    }
  }

  /* ---------- Add to cart without leaving the page ---------- */

  const cartPopup = () => document.querySelector('[data-cart-popup]');

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    const link = document.querySelector('[data-cart-link]');
    const popup = cartPopup();
    if (link && popup) {
      const label = count === 1 ? popup.dataset.cartOne : popup.dataset.cartMany;
      link.setAttribute('aria-label', label.replace('#', String(count)));
    }
  };

  let popupTimer;
  const showCartPopup = (item, added, count) => {
    const popup = cartPopup();
    if (!popup) return;
    const img = popup.querySelector('[data-cart-popup-img]');
    if (item.image) {
      const src = new URL(item.image, location.href);
      src.searchParams.set('width', '240');
      img.src = src.href;
      img.alt = '';
      img.hidden = false;
    } else {
      img.hidden = true;
    }
    popup.querySelector('[data-cart-popup-title]').textContent = item.product_title;
    const variant = popup.querySelector('[data-cart-popup-variant]');
    const hasVariant = item.variant_title && !item.product_has_only_default_variant;
    variant.textContent = hasVariant ? item.variant_title : '';
    variant.hidden = !hasVariant;
    popup.querySelector('[data-cart-popup-qty]').textContent = '×' + added;
    popup.querySelector('[data-cart-popup-count]').textContent = String(count);

    popup.hidden = false;
    popup.classList.remove('leaving');
    popup.querySelector('[data-cart-popup-heading]').focus({ preventScroll: true });

    clearTimeout(popupTimer);
    const later = () => {
      clearTimeout(popupTimer);
      popupTimer = setTimeout(hideCartPopup, 9000);
    };
    later();
    popup.onmouseenter = popup.onfocusin = () => clearTimeout(popupTimer);
    popup.onmouseleave = later;
  };

  const hideCartPopup = () => {
    const popup = cartPopup();
    if (!popup || popup.hidden) return;
    popup.classList.add('leaving');
    setTimeout(() => {
      popup.hidden = true;
      popup.classList.remove('leaving');
    }, 220);
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-popup-close]')) hideCartPopup();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideCartPopup();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add\/?$/.test(action) || form.matches('[data-no-ajax]')) return;
    const popup = cartPopup();
    if (!popup) return;

    event.preventDefault();
    const button = form.querySelector('[type="submit"][name="add"], [type="submit"]:not([name])');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const body = new FormData(form);
    const added = Number(body.get('quantity')) || 1;

    try {
      if (!(await ensureQueueInfo())) return;
      if (!(await allowAdd(form, added))) return;

      const res = await fetch(action + '.js', {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const data = await res.json();
      if (!res.ok) {
        toast(data.description || data.message || popup.dataset.error, 'error');
        return;
      }
      const item = data.items ? data.items[0] : data;
      const cart = await fetch(popup.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      updateCartCount(cart.item_count);
      showCartPopup(item, added, cart.item_count);
      refreshProductLimit(cart);
      form.dispatchEvent(
        new CustomEvent('cart:added', { bubbles: true, detail: { item, cart, variantId: Number(body.get('id')) } })
      );
    } catch (err) {
      toast(popup.dataset.error, 'error');
    } finally {
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = button.hasAttribute('data-sold-out') || Boolean(button.dataset.limitReached);
      }
    }
  });

  /* ---------- Share ---------- */

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-share]');
    if (!btn) return;
    const url = btn.dataset.shareUrl || location.href;
    const title = btn.dataset.shareTitle || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (e) {
        // The visitor closed the share sheet.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast(btn.dataset.copied, 'ok');
    } catch (e) {
      window.prompt(btn.dataset.copyPrompt, url);
    }
  });
})();
